import { BufferAttribute, BufferGeometry, Object3D } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { ProjectionGenerator } from '@spazious/dollhouse-core';

import { isBufferAttribute, isBufferGeometry, isMesh } from '../utils/index.js';

const ANGLE_THRESHOLD = 90;

export class EdgeProjection {
  buffer: BufferGeometry = null!;

  setFromObject(object: Object3D, onProgressCallback?: (progress: number) => void): this {
    const geometries: BufferGeometry[] = [];
    object.updateWorldMatrix(true, true);
    object.traverse((c) => {
      if (isMesh(c) && isBufferGeometry(c.geometry)) {
        const clone = c.geometry.clone();
        clone.applyMatrix4(c.matrixWorld);
        for (const key in clone.attributes) {
          if (key !== 'position') {
            clone.deleteAttribute(key);
          }
        }

        geometries.push(clone);
      }
    });

    const mergedGeometry = mergeGeometries(geometries, false);
    this.setFromGeometry(mergedGeometry, onProgressCallback);

    return this;
  }

  setFromGeometry(geometry: BufferGeometry, onProgressCallback?: (progress: number) => void): this {
    const index = geometry.index ? (geometry.index.array as Float32Array).slice() : null;
    const position = (geometry.attributes.position.array as Float32Array).slice();

    const geometryCopy = new BufferGeometry();
    if (index) {
      geometryCopy.setIndex(new BufferAttribute(index, 1, false));
    }
    geometryCopy.setAttribute('position', new BufferAttribute(position, 3, false));

    const generator = new ProjectionGenerator();
    generator.angleThreshold = ANGLE_THRESHOLD;

    const task = generator.generate(geometryCopy, {
      onProgress: onProgressCallback,
    });

    let result = task.next();
    while (!result.done) {
      result = task.next();
    }

    if (!isBufferAttribute(result.value.attributes.position)) {
      throw new Error('ProjectionGeneratorWorker: Position attribute is not a BufferAttribute.');
    }

    const resultLines = result.value.attributes.position.array as Float32Array;

    this.buffer = new BufferGeometry();
    this.buffer.setAttribute('position', new BufferAttribute(resultLines, 3, false));

    return this;
  }
}
